import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { linkHorizontal } from 'd3-shape'
import { select } from 'd3-selection'
import { zoom, zoomIdentity, type ZoomTransform } from 'd3-zoom'
import type { ConfigurationGraph } from '../api/types'

/*
  The configuration, drawn.

  ----------------------------------------------------------------------------------
  WHY THREE COLUMNS AND NOT A FORCE LAYOUT

  Because the product's model already has an order: an ACCOUNT is a pool of cost, a
  DRIVER is the statistical basis it is spread on, and a COST CENTER is where it lands.
  Cost flows left to right and never the other way, so the reader's eye should too.

  A force simulation would throw that away. It jitters between renders, so the picture
  a person learned yesterday is not the picture they get today; it has no stable reading
  order; and it degrades into a hairball well before the §12 scale target of 200 cost
  centers. Nothing here needs a simulation — the positions are computed, so the same
  configuration draws identically every time, which is what makes it checkable.

  ----------------------------------------------------------------------------------
  WHAT ENCODES WHAT

  POSITION carries node type. Column 1 is accounts, column 2 drivers, column 3 cost
  centers, and each column has a heading. That is a stronger channel than color and it
  is available to every reader, including one who cannot distinguish two hues at all.

  COLOR IS THEREFORE FREE, and it is spent on the two things worth interrupting for:

    - PROBLEMS, in the semantic tokens. An account that routes nowhere, a driver whose
      percentages do not total 100, a cost center no driver reaches. Never color alone:
      every one of them also carries a glyph and says what is wrong in words.

    - EMPHASIS. Hovering or selecting anything lifts it and everything it connects to
      into the accent and drops the rest back. This is the only way to read one driver's
      reach out of a picture with several in it.

  There is deliberately NO categorical palette here. The brand ships eight categorical
  tokens and no three of them clear the colorblind-separation threshold against each
  other -- orange and green are 0.1 apart under protanopia -- so coloring nodes by type
  would have been decoration that fails for some readers while competing with the
  problem colors, which brand.css says must outrank everything.
*/

const COL_ACCOUNT = 0
const COL_DRIVER = 1
const COL_CENTER = 2

/** Row pitch. Tight enough to fit a real chart, loose enough to hit with a mouse. */
const ROW = 30
const NODE_H = 22
const COL_W = 290
const COL_GAP = 130
const PAD_X = 20
const PAD_Y = 34

type NodeKind = 'accountBand' | 'problemAccount' | 'driver' | 'costCenter'

interface LaidOutNode {
  key: string
  kind: NodeKind
  column: number
  x: number
  y: number
  width: number
  label: string
  /** Second line, smaller. The count, the code, the percentage. */
  detail: string
  /** Non-null when the node is reporting something wrong. */
  problem: { severity: 'error' | 'warn'; text: string } | null
  /** Rendered as a heading rather than a target — a non-allocable cost center. */
  isHeading: boolean
  indent: number
  /**
   * For the emphasis pass: which driver ids this node belongs to. A cost center reached
   * by three drivers has three, which is exactly why this is a list and not a field.
   */
  driverIds: number[]
  /** Set on cost center nodes only. What scopes a trace that started at one. */
  costCenterId?: number
  title: string
}

interface LaidOutLink {
  key: string
  from: LaidOutNode
  to: LaidOutNode
  driverId: number
  /** The cost center this link arrives at, where it has one. */
  costCenterId?: number
  label: string
}

export interface ConfigurationGraphViewProps {
  graph: ConfigurationGraph
  /** Cost centers to draw. The screen filters; this only draws. */
  costCenterIds: Set<number>
  /**
   * Driver ids, not one driver id.
   *
   * A cost center is reached by several drivers, and "what reaches this department?" is
   * the question this screen exists to answer — so pointing at the department has to
   * work. It cannot if the emphasis is a single id, which is what it was: cost centers
   * were inert, and the only way in was through a driver you had already identified.
   */
  onSelect: (focus: EmphasisFocus | null) => void
  selected: EmphasisFocus | null
}

/**
 * What is being traced, and from where.
 *
 * WHICH END YOU POINT AT CHANGES THE QUESTION, and a driver set alone cannot express
 * that. Pointing at a DRIVER asks "where does this go" — light its accounts and every
 * cost center it reaches. Pointing at a COST CENTER asks "what arrives here" — light the
 * drivers that reach it and their accounts, and NOT the other departments those drivers
 * also serve.
 *
 * That distinction is not cosmetic. Tracing from a cost center through its drivers and
 * back out again lights every sibling that shares any driver with it, which in a real
 * chart is most of them: the reader asked about one department and got the picture they
 * already had.
 */
export interface EmphasisFocus {
  /** Drivers on the lit path. */
  drivers: number[]
  /**
   * Set when the trace started at a cost center, and then the ONLY cost center lit.
   * Undefined when it started at a driver or an account band, where every cost center
   * the driver reaches is part of the answer.
   */
  costCenterId?: number
}

const curve = linkHorizontal<unknown, [number, number]>()
  .x((d) => d[0])
  .y((d) => d[1])

export function ConfigurationGraphView({
  graph,
  costCenterIds,
  onSelect,
  selected,
}: ConfigurationGraphViewProps) {
  const hostRef = useRef<HTMLDivElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const [width, setWidth] = useState(900)
  const [transform, setTransform] = useState<ZoomTransform>(zoomIdentity)
  const [hovered, setHovered] = useState<EmphasisFocus | null>(null)

  // Width from the element, not from a media query: this sits in a settings pane whose
  // width depends on the sidebar, not on the viewport.
  useLayoutEffect(() => {
    const host = hostRef.current
    if (!host) return

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry) setWidth(Math.max(600, entry.contentRect.width))
    })

    observer.observe(host)
    return () => observer.disconnect()
  }, [])

  const { nodes, links, height } = useLayout(graph, costCenterIds)

  /*
      d3 computes; React renders.

      The zoom behaviour is attached to the SVG element and its transform is kept in
      React state, applied to one <g>. d3 never appends or removes a node here. Letting
      it own the DOM would mean fighting StrictMode's double-mount and re-entering the
      same subtree twice, and there is nothing to gain -- the hard part of zoom is the
      wheel/pinch/drag arithmetic, which is exactly what d3-zoom is for.
  */
  useEffect(() => {
    const element = svgRef.current
    if (!element) return

    const behaviour = zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.3, 2.5])
      .on('zoom', (event) => setTransform(event.transform))

    const selection = select(element)
    selection.call(behaviour)

    // No double-click-to-zoom: double-clicking a node is a selection gesture here, and
    // a zoom stealing it makes the diagram feel like it is fighting the pointer.
    selection.on('dblclick.zoom', null)

    return () => {
      selection.on('.zoom', null)
    }
  }, [])

  const reset = useCallback(() => {
    const element = svgRef.current
    if (!element) return
    select(element).call(zoom<SVGSVGElement, unknown>().transform, zoomIdentity)
    setTransform(zoomIdentity)
  }, [])

  /*
      The emphasis set.

      Hover wins over selection so that exploring never requires deselecting first, and
      the two behave identically otherwise -- which is also what makes keyboard focus
      work: focus sets `hovered`, so a keyboard reader gets the same reading a mouse
      does rather than a lesser one.
  */
  const active = hovered ?? selected
  const activeSet = useMemo(
    () => (active === null ? null : new Set(active.drivers)),
    [active],
  )

  /**
   * Whether a node or link is on the traced path.
   *
   * `costCenterId` is the node's own id where it has one. When the trace started at a
   * cost center, every other cost center is dark however many drivers it shares.
   */
  const isLit = (driverIds: number[], costCenterId?: number) => {
    if (activeSet === null) return true
    if (!driverIds.some((id) => activeSet.has(id))) return false
    if (active?.costCenterId === undefined || costCenterId === undefined) return true
    return costCenterId === active.costCenterId
  }

  /**
   * On the SELECTED path, ignoring hover.
   *
   * The pale wash marks what a click pinned, so it survives the pointer moving away; the
   * border marks whatever is lit right now, hover included. Two states, two channels —
   * without the split, a hover would repaint the selection and there would be no way to
   * see what is still pinned.
   */
  const inSelection = (driverIds: number[], costCenterId?: number) => {
    if (selected === null) return false
    if (!driverIds.some((id) => selected.drivers.includes(id))) return false
    if (selected.costCenterId === undefined || costCenterId === undefined) return true
    return costCenterId === selected.costCenterId
  }

  const contentWidth = PAD_X * 2 + COL_W * 3 + COL_GAP * 2

  return (
    <div ref={hostRef} className="relative h-full min-h-0">
      <div className="absolute right-2 top-2 z-10 flex gap-1">
        <button
          type="button"
          className="btn btn-sm"
          onClick={reset}
          disabled={transform === zoomIdentity}
        >
          Reset view
        </button>
        {selected !== null && (
          <button type="button" className="btn btn-sm" onClick={() => onSelect(null)}>
            Clear selection
          </button>
        )}
      </div>

      <svg
        ref={svgRef}
        width="100%"
        height="100%"
        viewBox={`0 0 ${Math.max(width, contentWidth)} ${height + PAD_Y * 2}`}
        style={{ display: 'block', cursor: 'grab', touchAction: 'none' }}
        role="group"
        aria-label="Configuration graph: accounts route through drivers to cost centers"
      >
        <g transform={transform.toString()}>
          {/* Column headings. These are what make position readable as type. */}
          {(['Accounts', 'Drivers', 'Cost centers'] as const).map((heading, index) => (
            <text
              key={heading}
              x={PAD_X + index * (COL_W + COL_GAP)}
              y={PAD_Y - 14}
              className="col-head"
              style={{ fill: 'var(--color-faint)' }}
            >
              {heading}
            </text>
          ))}

          {/*
              Links first, so nodes sit on top of them.

              Hairlines in `faint`, which clears 3:1 against the panel -- `rule` is a
              shade off the surface and correct for a column divider, but a relationship
              is content and has to be visible. Lit links thicken to 2px and take the
              accent; the rest drop to 0.25 opacity rather than disappearing, because a
              connection that vanishes reads as a connection that is not there.
          */}
          {links.map((link) => {
            const lit = isLit([link.driverId], link.costCenterId)
            return (
              <path
                key={link.key}
                d={
                  curve({
                    source: [link.from.x + link.from.width, link.from.y + NODE_H / 2],
                    target: [link.to.x, link.to.y + NODE_H / 2],
                  }) ?? undefined
                }
                fill="none"
                stroke={lit && active !== null ? 'var(--color-accent)' : 'var(--color-faint)'}
                strokeWidth={lit && active !== null ? 2 : 1}
                opacity={lit ? 0.9 : 0.18}
              >
                <title>{link.label}</title>
              </path>
            )
          })}

          {nodes.map((node) => {
            const lit = isLit(node.driverIds, node.costCenterId)

            /*
                Anything with an edge is traceable. An unrouted account carries none, so
                it stays inert — there is nothing for it to trace to, and that is the
                point of it.

                A cost center passes its own id along, which is what narrows the trace to
                "what arrives here" instead of "everything these drivers touch".
            */
            const focus: EmphasisFocus | null =
              node.driverIds.length > 0
                ? { drivers: node.driverIds, costCenterId: node.costCenterId }
                : null

            return (
              <g
                key={node.key}
                transform={`translate(${node.x + node.indent}, ${node.y})`}
                opacity={lit ? 1 : 0.3}
                tabIndex={focus !== null ? 0 : undefined}
                role={focus !== null ? 'button' : undefined}
                aria-label={node.title}
                style={{ cursor: focus !== null ? 'pointer' : 'default', outline: 'none' }}
                onMouseEnter={() => focus !== null && setHovered(focus)}
                onMouseLeave={() => setHovered(null)}
                onFocus={() => focus !== null && setHovered(focus)}
                onBlur={() => setHovered(null)}
                onClick={() => focus !== null && onSelect(sameFocus(selected, focus) ? null : focus)}
                onKeyDown={(event) => {
                  if (focus !== null && (event.key === 'Enter' || event.key === ' ')) {
                    event.preventDefault()
                    onSelect(sameFocus(selected, focus) ? null : focus)
                  }
                }}
              >
                <title>{node.title}</title>

                {node.isHeading ? (
                  /*
                      A non-allocable cost center is a heading on the chart, not a target.
                      Drawn as a label with a rule under it rather than a box, because a
                      box would say "cost can land here" about a node where it cannot.
                  */
                  <>
                    <text
                      x={0}
                      y={NODE_H / 2 + 4}
                      style={{
                        fill: 'var(--color-muted)',
                        fontSize: 11,
                        fontWeight: 500,
                        letterSpacing: '0.04em',
                        textTransform: 'uppercase',
                      }}
                    >
                      {node.label}
                    </text>
                    <line
                      x1={0}
                      x2={node.width - node.indent}
                      y1={NODE_H - 2}
                      y2={NODE_H - 2}
                      stroke="var(--color-rule)"
                      strokeWidth={1}
                    />
                  </>
                ) : (
                  <>
                    <rect
                      width={node.width - node.indent}
                      height={NODE_H}
                      rx={5}
                      fill={
                        node.problem?.severity === 'error'
                          ? 'var(--color-error-wash)'
                          : node.problem?.severity === 'warn'
                            ? 'var(--color-warn-wash)'
                            : inSelection(node.driverIds, node.costCenterId)
                              ? 'var(--color-accent-wash)'
                              : 'var(--color-panel)'
                      }
                      stroke={
                        node.problem?.severity === 'error'
                          ? 'var(--color-error)'
                          : node.problem?.severity === 'warn'
                            ? 'var(--color-warn)'
                            : lit && active !== null
                              ? 'var(--color-accent)'
                              : 'var(--color-rule)'
                      }
                      strokeWidth={node.problem || (lit && active !== null) ? 1.5 : 1}
                    />

                    {/*
                        The glyph. Colour never carries the problem on its own — a reader
                        who cannot see the red still gets a marker here, the word in the
                        tooltip, and a row in the table view.
                    */}
                    {node.problem && (
                      <text
                        x={7}
                        y={NODE_H / 2 + 4}
                        style={{
                          fill:
                            node.problem.severity === 'error'
                              ? 'var(--color-error)'
                              : 'var(--color-warn)',
                          fontSize: 11,
                          fontWeight: 700,
                        }}
                        aria-hidden="true"
                      >
                        !
                      </text>
                    )}

                    <text
                      x={node.problem ? 18 : 8}
                      y={NODE_H / 2 + 4}
                      style={{ fill: 'var(--color-ink)', fontSize: 12 }}
                    >
                      {/*
                          Measured against what the detail actually occupies, not a fixed
                          allowance. A guessed 62px let 'Facilities Footprint' run into its
                          own code — the longest name and the longest code being the same
                          row is not a coincidence, it is the normal case.
                      */}
                      {clip(
                        node.label,
                        node.width -
                          node.indent -
                          (node.problem ? 18 : 8) -
                          8 -
                          (node.detail ? node.detail.length * 6.6 + 10 : 0),
                      )}
                    </text>

                    <text
                      x={node.width - node.indent - 8}
                      y={NODE_H / 2 + 4}
                      textAnchor="end"
                      style={{
                        fill: 'var(--color-faint)',
                        fontSize: 11,
                        fontFamily: 'var(--font-mono)',
                      }}
                    >
                      {node.detail}
                    </text>
                  </>
                )}
              </g>
            )
          })}
        </g>
      </svg>
    </div>
  )
}

/**
 * Depth-first order: parents before their children, siblings by code.
 *
 * Any node whose parent was filtered out is treated as a root, so a filtered view still
 * lists everything it was given rather than silently dropping the orphans.
 */
function orderByTree(
  centers: ConfigurationGraph['costCenters'],
): ConfigurationGraph['costCenters'] {
  const present = new Set(centers.map((c) => c.costCenterId))
  const children = new Map<number | null, ConfigurationGraph['costCenters']>()

  for (const centre of centers) {
    const parent =
      centre.parentCostCenterId !== null && present.has(centre.parentCostCenterId)
        ? centre.parentCostCenterId
        : null

    const bucket = children.get(parent)
    if (bucket) bucket.push(centre)
    else children.set(parent, [centre])
  }

  for (const bucket of children.values()) {
    bucket.sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }))
  }

  const ordered: ConfigurationGraph['costCenters'] = []

  const walk = (parent: number | null) => {
    for (const centre of children.get(parent) ?? []) {
      ordered.push(centre)
      walk(centre.costCenterId)
    }
  }

  walk(null)
  return ordered
}

/** Whether a click lands on the trace that is already active, and so clears it. */
function sameFocus(a: EmphasisFocus | null, b: EmphasisFocus): boolean {
  return (
    a !== null &&
    a.costCenterId === b.costCenterId &&
    a.drivers.length === b.drivers.length &&
    a.drivers.every((id) => b.drivers.includes(id))
  )
}

/**
 * Trims a label to the width it has, in characters.
 *
 * SVG has no `text-overflow`, and a clipPath would crop mid-glyph — an anti-pattern in
 * its own right, because the first thing a reader loses is the end of the name that
 * distinguishes it from its sibling. An ellipsis at least says the name continues, and
 * the full text is in the tooltip and the table.
 */
function clip(text: string, pixels: number): string {
  const max = Math.max(4, Math.floor(pixels / 6.4))
  return text.length <= max ? text : `${text.slice(0, max - 1)}…`
}

/**
 * Positions every node and link.
 *
 * Deterministic and pure: the same graph in gives the same picture out, which is what
 * lets somebody compare today's screen against yesterday's screenshot.
 */
function useLayout(graph: ConfigurationGraph, costCenterIds: Set<number>) {
  return useMemo(() => {
    const nodes: LaidOutNode[] = []
    const links: LaidOutLink[] = []

    const columnX = (column: number) => PAD_X + column * (COL_W + COL_GAP)

    // ------------------------------------------------------------- cost centers
    /*
        Ordered by walking the tree, NOT by MaterializedPath.

        The path is '/7/8/9/' — ids, as text. Sorting it lexically puts '/7/8/10/' before
        '/7/8/9/', because '1' sorts before '9', so the tenth child of a node jumps ahead
        of its ninth. In the demo that renders Ancillary Services above Inpatient Routine
        Service and 3100 above 3000, and at any real size sibling order is simply
        arbitrary — every id that crosses a power of ten reorders the chart.

        Depth-first from the roots, siblings by code, gives the order somebody reading a
        chart of accounts expects. Done here rather than in the procedure because display
        order is the client's business, and because a stable path sort would need
        zero-padded ids the schema does not have.

        NOTE: alloc.usp_CostCenter_* orders by MaterializedPath too, so the cost center
        tree screen has the same defect. Not fixed here — it is a separate change to a
        shipped procedure.
    */
    const centers = orderByTree(
      graph.costCenters.filter((c) => costCenterIds.has(c.costCenterId)),
    )

    const centerNodes = new Map<number, LaidOutNode>()

    centers.forEach((centre, index) => {
      const node: LaidOutNode = {
        key: `cc-${centre.costCenterId}`,
        kind: 'costCenter',
        column: COL_CENTER,
        x: columnX(COL_CENTER),
        y: PAD_Y + index * ROW,
        width: COL_W,
        label: `${centre.code} ${centre.name}`,
        /*
            Nothing. Deliberately.

            Two figures were tried here and both were wrong. The SUM OF THE DRIVERS'
            SHARES is not a quantity at all -- each driver's percentages total 100 across
            its OWN targets, so adding one node's shares across drivers adds fractions
            with unrelated denominators, routinely exceeds 100, and reads as a fault when
            nothing is wrong. Replacing it with a terse driver count ('3->') was no
            better: an abbreviation whose unit the reader has to guess is the same defect
            in a smaller font, and this product has already shipped one of those.

            How many drivers reach a cost center is legible from the lines arriving at it,
            stated in words in the tooltip, and listed in the table. The individual shares
            ARE meaningful -- one denominator each -- and they live on the links, where
            they belong.
        */
        detail: '',
        problem: centre.hasNoCoverage
          ? {
              severity: 'warn',
              text: 'No driver targets this cost center. It will receive nothing.',
            }
          : null,
        isHeading: !centre.isAllocable,
        // Indent by depth so the hierarchy is legible without drawing tree edges into a
        // flow diagram, which would put two different kinds of line on one picture.
        indent: Math.min(centre.depth, 4) * 10,
        driverIds: [],
        costCenterId: centre.costCenterId,
        title: buildCentreTitle(centre),
      }

      centerNodes.set(centre.costCenterId, node)
      nodes.push(node)
    })

    const centreHeight = centers.length * ROW

    // ------------------------------------------------------------------ drivers
    /*
        Spread evenly over the height the cost-center column occupies, rather than
        stacked from the top.

        With four drivers against forty cost centers, top-stacking puts every line in the
        top tenth of the picture and leaves the rest empty. Spreading keeps the link
        angles shallow, which is the whole readability of a layered diagram.
    */
    const driverCount = Math.max(graph.drivers.length, 1)
    const driverSpan = Math.max(centreHeight, driverCount * ROW)

    const accountEdgeByDriver = new Map(graph.accountEdges.map((e) => [e.costDriverId, e]))

    const driverNodes = new Map<number, LaidOutNode>()

    graph.drivers.forEach((driver, index) => {
      const y =
        PAD_Y + ((index + 0.5) * driverSpan) / driverCount - NODE_H / 2

      const broken =
        driver.status !== 'Retired' &&
        (driver.healthState === 'Incomplete' || driver.healthState === 'NoAllocations')

      const node: LaidOutNode = {
        key: `d-${driver.costDriverId}`,
        kind: 'driver',
        column: COL_DRIVER,
        x: columnX(COL_DRIVER),
        y,
        width: COL_W,
        label: driver.name,
        detail: driver.code,
        problem: broken
          ? {
              severity: 'error',
              text:
                driver.healthState === 'NoAllocations'
                  ? 'This driver has no effective allocations. A run resolving it would fail.'
                  : `Percentages total ${trim(driver.totalPercent)}, not 100. QC refuses the run.`,
            }
          : null,
        isHeading: false,
        indent: 0,
        driverIds: [driver.costDriverId],
        title: buildDriverTitle(driver, accountEdgeByDriver.get(driver.costDriverId)),
      }

      driverNodes.set(driver.costDriverId, node)
      nodes.push(node)
    })

    // --------------------------------------------------- account bands, per driver
    /*
        One node per driver saying how many accounts route through it -- not one node per
        account. A driver with 4,987 accounts is one band; the 4,987 identical edges say
        nothing the number does not.
    */
    graph.drivers.forEach((driver) => {
      const edge = accountEdgeByDriver.get(driver.costDriverId)
      const driverNode = driverNodes.get(driver.costDriverId)
      if (!driverNode) return

      const count = edge?.accountCount ?? 0

      const node: LaidOutNode = {
        key: `ab-${driver.costDriverId}`,
        kind: 'accountBand',
        column: COL_ACCOUNT,
        x: columnX(COL_ACCOUNT),
        y: driverNode.y,
        width: COL_W,
        label: count === 1 ? '1 account' : `${count} accounts`,
        detail: edge && edge.partialCount > 0 ? `${edge.partialCount} partial` : '',
        /*
            No problem marker, deliberately.

            A driver nothing routes through is QC-D014, and QC-D014 is INFO. It was
            painted as a warning here, which is this screen describing a fault differently
            from the dashboard that owns it — the one thing it must not do. A driver
            defined and not yet used is a normal state halfway through building a
            configuration, not something to interrupt for.

            It still reads as empty, because the band says '0 accounts', no line leaves
            it, and the tooltip says so in words. That is the Info treatment: available,
            not shouted.
        */
        problem: null,
        isHeading: false,
        indent: 0,
        driverIds: [driver.costDriverId],
        title:
          count === 0
            ? `No account routes through ${driver.name}.`
            : `${count} account${count === 1 ? '' : 's'} route through ${driver.name}` +
              (edge && edge.partialCount > 0
                ? `, ${edge.partialCount} of them only partly.`
                : ', all of them in full.'),
      }

      nodes.push(node)

      if (count > 0) {
        links.push({
          key: `l-ab-${driver.costDriverId}`,
          from: node,
          to: driverNode,
          driverId: driver.costDriverId,
          label: node.title,
        })
      }
    })

    // --------------------------------------------------------- unrouted accounts
    /*
        Below the bands, with a gap, and deliberately NOT connected to anything.

        That is the entire point of drawing them: an account with a balance and no driver
        is money that would land nowhere, and QC-A001 blocks the run over it. A node with
        no line leaving it is the most direct way a picture can say so.
    */
    const problemTop = PAD_Y + driverSpan + ROW

    graph.problemAccounts.forEach((account, index) => {
      nodes.push({
        key: `pa-${account.accountId}`,
        kind: 'problemAccount',
        column: COL_ACCOUNT,
        x: columnX(COL_ACCOUNT),
        y: problemTop + index * ROW,
        width: COL_W,
        label: `${account.accountNumber} ${account.name}`,
        detail: account.problem === 'Unrouted' ? 'none' : `${trim(account.totalPercent)}%`,
        problem: {
          severity: 'error',
          text:
            account.problem === 'Unrouted'
              ? 'Routes through no driver. QC-A001 refuses the run: the balance would be allocated nowhere.'
              : `Routes ${trim(account.totalPercent)}% of its balance, not 100%. QC-A002 refuses the run.`,
        },
        isHeading: false,
        indent: 0,
        driverIds: [],
        title:
          `${account.accountNumber} ${account.name} — ` +
          (account.problem === 'Unrouted'
            ? 'routes through no driver.'
            : `routes ${trim(account.totalPercent)}%, not 100%.`),
      })
    })

    // ------------------------------------------------- driver -> cost center links
    for (const edge of graph.driverEdges) {
      const from = driverNodes.get(edge.costDriverId)
      const to = centerNodes.get(edge.costCenterId)

      // A filtered-out cost center drops its links with it rather than leaving a line
      // running to nothing.
      if (!from || !to) continue

      to.driverIds.push(edge.costDriverId)

      links.push({
        key: `l-d-${edge.costDriverId}-${edge.costCenterId}`,
        from,
        to,
        driverId: edge.costDriverId,
        costCenterId: edge.costCenterId,
        label: `${from.label} → ${to.label}: ${trim(edge.percent)}%`,
      })
    }

    const height = Math.max(
      centreHeight,
      driverSpan,
      graph.problemAccounts.length > 0
        ? problemTop - PAD_Y + graph.problemAccounts.length * ROW
        : 0,
    )

    return { nodes, links, height }
  }, [graph, costCenterIds])
}

/** Percentages without trailing zeros — `34.220532`, not `34.220532000000`. */
function trim(value: number): string {
  return Number(value.toFixed(6)).toString()
}

function buildCentreTitle(centre: ConfigurationGraph['costCenters'][number]): string {
  if (!centre.isAllocable) {
    return `${centre.code} ${centre.name} — a heading. Marked not allocable, so nothing posts to it directly.`
  }

  if (centre.targetingDriverCount === 0) {
    return `${centre.code} ${centre.name} — allocable, and no driver targets it. It will receive nothing.`
  }

  return (
    `${centre.code} ${centre.name} — targeted by ${centre.targetingDriverCount} driver` +
    `${centre.targetingDriverCount === 1 ? '' : 's'}. Hover a link for that driver's share.`
  )
}

function buildDriverTitle(
  driver: ConfigurationGraph['drivers'][number],
  edge: ConfigurationGraph['accountEdges'][number] | undefined,
): string {
  const reach =
    `${driver.name} (${driver.code}) — ${driver.driverType}, ${driver.status}. ` +
    `Targets ${driver.costCenterCount} cost center${driver.costCenterCount === 1 ? '' : 's'}; ` +
    `${edge?.accountCount ?? 0} account${(edge?.accountCount ?? 0) === 1 ? '' : 's'} route through it.`

  if (driver.healthState === 'Ok' || driver.healthState === 'Retired') {
    return reach
  }

  if (driver.healthState === 'Unevaluated') {
    // A SQL driver's targets are not knowable without executing the query, so the
    // health column says so rather than guessing.
    return `${reach} Its targets are only known once the query runs.`
  }

  return `${reach} Percentages total ${trim(driver.totalPercent)}, not 100.`
}
